/**
 * @fileoverview Tactical annotation geometry calculations.
 */

import type { Coordinate } from 'ol/coordinate';

/**
 * The corners required to make the building sides lines.
 */
export const AREA_CORNERS = 4;

export class TacticalAnnotationCalculator {
  /**
   * Calculates the signed area of a polygon
   * Positive area = counter-clockwise, negative = clockwise.
   */
  static calculateArea(coordinates: Coordinate[]): number {
    let area = 0;
    for (let index = 0; index < coordinates.length; index++) {
      const nextIndex = (index + 1) % coordinates.length;
      const current = coordinates[index];
      const next = coordinates[nextIndex];
      const [currentX, currentY] = current;
      const [nextX, nextY] = next;
      if (
        currentX === undefined
        || currentY === undefined
        || nextX === undefined
        || nextY === undefined
      ) {
        continue;
      }
      area += currentX * nextY;
      area -= nextX * currentY;
    }

    return area / 2;
  }

  /**
   * Calculates the midpoint of a line segment.
   */
  static calculateMidpoint(start: Coordinate, end: Coordinate): Coordinate {
    const [startX, startY] = start;
    const [endX, endY] = end;
    if (
      startX === undefined
      || startY === undefined
      || endX === undefined
      || endY === undefined
    ) {
      return [0, 0];
    }
    return [
      (startX + endX) / 2,
      (startY + endY) / 2,
    ];
  }

  /**
   * Closes a polygon by appending the first coordinate to the end.
   */
  static closePolygon(coordinates: Coordinate[]): Coordinate[] {
    if (coordinates.length === 0) {
      return [...coordinates];
    }
    const firstCoordinate = coordinates[0];
    return [...coordinates, firstCoordinate];
  }

  /**
   * Finds the closest matching polygon edge for a given line segment.
   * Returns the index of the polygon edge that best matches the line endpoints.
   */
  static findClosestEdgeIndex(
    lineStart: Coordinate,
    lineEnd: Coordinate,
    polygonCoords: Coordinate[],
  ): { closestIndex: number } {
    let closestIndex = 0;
    let minimumDistance = Infinity;

    for (let edgeIndex = 0; edgeIndex < AREA_CORNERS; edgeIndex++) {
      const polygonStart = polygonCoords[edgeIndex];
      const polygonEnd = polygonCoords[edgeIndex + 1];
      const [startX, startY] = polygonStart;
      const [endX, endY] = polygonEnd;
      if (
        startX === undefined
        || startY === undefined
        || endX === undefined
        || endY === undefined
      ) {
        continue;
      }

      const [lineStartX, lineStartY] = lineStart;
      const [lineEndX, lineEndY] = lineEnd;
      if (
        lineStartX === undefined
        || lineStartY === undefined
        || lineEndX === undefined
        || lineEndY === undefined
      ) {
        continue;
      }
      const distanceFromStart = Math.sqrt(
        (lineStartX - startX) ** 2
        + (lineStartY - startY) ** 2,
      );
      const distanceFromEnd = Math.sqrt(
        (lineEndX - endX) ** 2
        + (lineEndY - endY) ** 2,
      );

      const totalDistance = distanceFromStart + distanceFromEnd;

      if (totalDistance < minimumDistance) {
        minimumDistance = totalDistance;
        closestIndex = edgeIndex;
      }
    }

    return {
      closestIndex,
    };
  }

  /**
   * Finds the index of the existing line that best matches the new side coordinates.
   * Returns the array index of the best matching line in existingLines.
   */
  static findMatchingLineIndex(
    newCoordinates: Coordinate[],
    existingLines: Array<{ coords: Coordinate[]; index: number }>,
  ): { closestIndex: number; line: { coords: Coordinate[]; index: number } } {
    if (newCoordinates.length < 2) {
      if (existingLines.length === 0) {
        return {
          closestIndex: 0,
          line: { coords: [], index: 0 },
        };
      }
      return {
        closestIndex: 0,
        line: existingLines[0],
      };
    }
    const startCoordinate = newCoordinates[0];
    const endCoordinate = newCoordinates[1];
    const newMidpoint = this.calculateMidpoint(startCoordinate, endCoordinate);

    let closestIndex = 0;
    let minimumDistance = Infinity;

    for (const [index, existingLine] of existingLines.entries()) {
      if (existingLine.coords.length < 2) {
        continue;
      }
      const existingStart = existingLine.coords[0];
      const existingEnd = existingLine.coords[1];
      const oldMidpoint = this.calculateMidpoint(
        existingStart,
        existingEnd,
      );

      const [newMidpointX, newMidpointY] = newMidpoint;
      const [oldMidpointX, oldMidpointY] = oldMidpoint;
      if (
        newMidpointX === undefined
        || newMidpointY === undefined
        || oldMidpointX === undefined
        || oldMidpointY === undefined
      ) {
        continue;
      }
      const distance = Math.sqrt(
        (newMidpointX - oldMidpointX) ** 2
        + (newMidpointY - oldMidpointY) ** 2,
      );

      if (distance < minimumDistance) {
        minimumDistance = distance;
        closestIndex = index;
      }
    }

    if (existingLines.length === 0) {
      return {
        closestIndex,
        line: { coords: [], index: 0 },
      };
    }
    const selectedLine = existingLines[closestIndex] ?? existingLines[0];
    return {
      closestIndex,
      line: selectedLine,
    };
  }

  /**
   * Normalizes polygon direction by reversing the coordinates if the polygon has a negative area.
   */
  static normalizeDirection(coordinates: Coordinate[]): Coordinate[] {
    const area = this.calculateArea(coordinates);
    return area < 0 ? [...coordinates].reverse() : coordinates;
  }
}
